import { WorkspaceSubscriptionPlan } from '@airimpact/types/modelEnums'
import { Box, Button, Chip, Typography, Skeleton } from '@mui/material'
import moment from 'moment'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { PlanData, SubscriptionProvider, useTierSelectionContext } from '../../hooks/useTierSelection'
import Alert from '../Alert'

interface IProps {
  isLoading: boolean
  setIsLoading: Dispatch<SetStateAction<boolean>>
  isManageUsageScreen?: boolean
  planDetails: PlanData
  selectedInterval: string
  isTrial: boolean
  numberOfUsers: number
  numberOfProjects: number
  maximumNumberOfUsers: number
  maximumNumberOfProjects: number
  defaultNumberOfProjects: number
  currentPlanProvider?: SubscriptionProvider
  onAmountExceeded: (isAmountExceeded: boolean) => void
  isSubscriptionUpgrading: boolean | undefined
  setIsSubscriptionUpgrading: Dispatch<SetStateAction<boolean | undefined>>
  selectedPlanId: string
  selectedAddonSeatId: string
  selectedAddonProjectId: string
  amountOfAdditionalSeats: number
  amountOfAdditionalProjects: number
  isTierView: boolean | undefined
  isCanceledSubscription: boolean
}

interface PreviewPayloadItem {
  product_id: string
  quantity: number
}

interface PreviewPayload {
  items: PreviewPayloadItem[]
}

interface IPreviewResponseItem {
  amount: number
  amount_formatted: string
  currency: string
  description: string
  product_id: string
  quantity: number
}

function TermOfUseButton() {
  return (
    <Button
      onClick={() => window.open('https://airimpact.co/platform-privacy-policy', '_blank')}
      variant="text"
      focusRipple={false}
      sx={{ padding: 0, margin: 0, textDecoration: 'underline', color: 'primary.600' }}
    >
      Terms of Use.
    </Button>
  )
}

export function TierSelectionSummary(props: IProps) {
  const {
    isLoading,
    setIsLoading,
    isManageUsageScreen,
    planDetails,
    selectedInterval,
    isTrial,
    numberOfUsers,
    numberOfProjects,
    maximumNumberOfUsers,
    maximumNumberOfProjects,
    defaultNumberOfProjects,
    currentPlanProvider,
    onAmountExceeded,
    isSubscriptionUpgrading,
    setIsSubscriptionUpgrading,
    selectedPlanId,
    selectedAddonSeatId,
    selectedAddonProjectId,
    amountOfAdditionalSeats,
    amountOfAdditionalProjects,
    isTierView,
    isCanceledSubscription,
  } = props

  const [totalAmount, setTotalAmount] = useState<string>('0')
  const [credits, setCredits] = useState<string>('0')
  const [totals, setTotals] = useState<{
    totalAmountPerPlan: string
    totalAmountForSeats: string
    totalAmountForProjects: string
  }>({
    totalAmountPerPlan: '0',
    totalAmountForSeats: '0',
    totalAmountForProjects: '0',
  })

  const { subscriptionPlans, subscriptionAddons, getSubscriptionPricesPreview, currentPlanName } =
    useTierSelectionContext()

  const isPremium =
    (isTierView && planDetails.plan === WorkspaceSubscriptionPlan.Premium) ||
    (isManageUsageScreen && planDetails.plan === WorkspaceSubscriptionPlan.Premium) ||
    (!isManageUsageScreen && planDetails.plan === WorkspaceSubscriptionPlan.Premium)

  const currentSubscriptionHasNonTrialFreePlan = !isTrial && currentPlanName === WorkspaceSubscriptionPlan.Free

  // TODO: to recheck the price and fix if yearly and monthly are different
  const priceDetails = planDetails.intervals.find((intervalDetails) => intervalDetails.interval === selectedInterval)
  const amount = priceDetails?.unit_amount_decimal ? Number(priceDetails.unit_amount_decimal) / 100 : 0
  const pricePerMonth = selectedInterval === 'year' ? amount / 12 : amount
  const currentPlanId = subscriptionPlans.find(
    (plan) => plan.plan.toLocaleLowerCase() === currentPlanName?.toLocaleLowerCase(),
  )?._id

  const addonSeat = subscriptionAddons.find((addon) => addon.plan === planDetails.plan && addon.key === 'Seat')
  const pricePerMonthForSeat =
    Number(
      (selectedInterval === 'year'
        ? addonSeat?.intervals.find((item) => item.interval === 'year')
        : addonSeat?.intervals.find((item) => item.interval === 'month')
      )?.unit_amount_formatted.replace(/,/g, ''),
    ) || 0

  const addonProject = subscriptionAddons.find((addon) => addon.key === 'Project')
  const pricePerMonthForProject =
    Number(
      (selectedInterval === 'year'
        ? addonProject?.intervals.find((item) => item.interval === 'year')
        : addonProject?.intervals.find((item) => item.interval === 'month')
      )?.unit_amount_formatted.replace(/,/g, ''),
    ) || 0

  const currentDate = moment()
  const renewDate =
    !isTrial || currentSubscriptionHasNonTrialFreePlan
      ? selectedInterval === 'year'
        ? currentDate.add(1, 'years')
        : currentDate.add(1, 'months')
      : currentDate.add(1, 'months')
  const formattedRenewDate = renewDate.format('MMMM D, YYYY')
  const endDate = isTrial ? currentPlanProvider?.trial.end : currentPlanProvider?.period.end

  const totalForPlans = pricePerMonth * (selectedInterval === 'year' ? 12 : 1)
  const totalForSeats = pricePerMonthForSeat * amountOfAdditionalSeats
  const totalForProjects = isPremium ? pricePerMonthForProject * amountOfAdditionalProjects : 0
  const totalForPlansWithSeats = String(totalForPlans + totalForSeats)
  const totalAmountAfterRenewal = String(totalForPlans + totalForSeats + totalForProjects)

  useEffect(() => {
    const totals = {
      totalAmountPerPlan: '0',
      totalAmountForSeats: '0',
      totalAmountForProjects: '0',
    }

    // Payload should contain those items that should eb present in a new subscription
    let payload: PreviewPayload | undefined

    const isSubscriptionModified =
      currentPlanId !== selectedPlanId ||
      maximumNumberOfUsers !== numberOfUsers ||
      maximumNumberOfProjects !== numberOfProjects

    if (isCanceledSubscription) {
      setTotalAmount(totalAmountAfterRenewal)
      return
    }

    if (selectedInterval !== currentPlanProvider?.plan.interval) {
      setTotalAmount('0')
    }

    if (currentSubscriptionHasNonTrialFreePlan) {
      setIsSubscriptionUpgrading(true)
      setTotalAmount(totalAmountAfterRenewal)
      return
    }

    if (!isSubscriptionModified || isTrial || selectedInterval !== currentPlanProvider?.plan.interval) {
      setTotals(totals)
      setTotalAmount('0')
    } else {
      if (currentPlanId !== selectedPlanId) {
        payload = {
          items: [
            ...(payload?.items || []),
            {
              product_id: selectedPlanId,
              quantity: 1,
            },
          ],
        }
      } else {
        payload = {
          items: [
            ...(payload?.items || []),
            {
              product_id: currentPlanId,
              quantity: 1,
            },
          ],
        }
      }

      if (isManageUsageScreen && maximumNumberOfUsers !== numberOfUsers) {
        payload = {
          items: [
            ...(payload?.items || []),
            {
              product_id: selectedAddonSeatId,
              quantity: amountOfAdditionalSeats >= 0 ? amountOfAdditionalSeats : 0,
            },
          ],
        }
      }

      if (!isManageUsageScreen) {
        payload = {
          items: [
            ...(payload?.items || []),
            {
              product_id: selectedAddonSeatId,
              quantity: numberOfUsers - 1 >= 0 ? numberOfUsers - 1 : 0,
            },
          ],
        }
      }

      if (isPremium) {
        payload = {
          items: [
            ...(payload?.items || []),
            {
              product_id: selectedAddonProjectId,
              quantity: amountOfAdditionalProjects >= 0 ? amountOfAdditionalProjects : 0,
            },
          ],
        }
      }
    }

    if (payload && payload.items.length > 0) {
      const fetchData = async () => {
        try {
          setIsLoading(true)
          const data = await getSubscriptionPricesPreview(payload)

          if (Number(data.amount_due_formatted) < 0) {
            setIsSubscriptionUpgrading(false)
            setTotalAmount('0')
          } else {
            const plansWithSeats = data.items.filter(
              (item: IPreviewResponseItem) => item.product_id !== selectedAddonProjectId,
            )
            const seats = data.items.filter((item: IPreviewResponseItem) => item.product_id === selectedAddonSeatId)
            const projects = data.items.filter(
              (item: IPreviewResponseItem) => item.product_id === selectedAddonProjectId,
            )

            const totalAmountPerPlan = plansWithSeats.reduce(
              (sum: number, item: IPreviewResponseItem) =>
                sum + (Number(item.amount_formatted) >= 0 ? Number(item.amount_formatted) : 0),
              0,
            )

            const totalAmountForSeats = seats.reduce(
              (sum: number, item: IPreviewResponseItem) =>
                sum + (Number(item.amount_formatted) >= 0 ? Number(item.amount_formatted) : 0),
              0,
            )

            const totalAmountForProjects = projects.reduce(
              (sum: number, item: IPreviewResponseItem) =>
                sum + (Number(item.amount_formatted) >= 0 ? Number(item.amount_formatted) : 0),
              0,
            )

            const credits = data.items.reduce(
              (sum: number, item: IPreviewResponseItem) =>
                Number(item.amount_formatted) < 0 ? sum + Number(item.amount_formatted) : sum,
              0,
            )

            setTotalAmount(data.amount_due_formatted)
            setTotals((prevTotals) => ({
              ...prevTotals,
              totalAmountPerPlan:
                !isManageUsageScreen && totalAmountPerPlan > 0 ? String(totalAmountPerPlan.toFixed(2)) : '0',
              totalAmountForSeats: totalAmountForSeats > 0 ? String(totalAmountForSeats.toFixed(2)) : '0',
              totalAmountForProjects: totalAmountForProjects > 0 ? String(totalAmountForProjects.toFixed(2)) : '0',
            }))
            setCredits(String(credits))
            setIsSubscriptionUpgrading(true)
          }
          setIsLoading(false)
        } catch (error) {
          console.error('Error when fetching preview data:', error)
          setIsLoading(false)
        }
      }

      setTimeout(() => {
        fetchData()
      }, 200)
    } else {
      setIsSubscriptionUpgrading(undefined)
    }
  }, [
    selectedInterval,
    pricePerMonth,
    numberOfUsers,
    numberOfProjects,
    amountOfAdditionalSeats,
    amountOfAdditionalProjects,
  ])

  const isAmountExceeded = Number(totalAmount) > 999999.99

  useEffect(() => {
    onAmountExceeded(isAmountExceeded)
  }, [isAmountExceeded, onAmountExceeded])

  return (
    <Box
      display="flex"
      flexDirection="column"
      p={2}
      border="1px solid"
      borderColor="primary.50"
      bgcolor="primary.25"
      width="383px"
      borderRadius="8px"
      boxSizing="border-box"
    >
      <Typography color="primary.800" variant="h5" mb={2}>
        Summary
      </Typography>

      {/* TODO reuse within existing component for all - pass number of projects and users */}
      {!isManageUsageScreen && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingY: 2,
            borderBottom: '1px dotted',
            borderColor: 'grey.300',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography color="grey.700" variant="body1">
              {planDetails.plan} {selectedInterval === 'year' ? 'Annual' : 'Monthly'}
              {isTrial && (
                <Chip
                  label="Free trial"
                  sx={{
                    marginLeft: 1,
                    borderRadius: '16px',
                    backgroundColor: '#e5edf7',
                    color: '#31587c',
                    fontSize: '12px',
                    height: '24px',
                  }}
                />
              )}
            </Typography>

            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={240} height={16} sx={{ marginTop: 1 }} />
            ) : (
              <Typography color="graygreen.700" variant="caption">
                ${pricePerMonth} USD x {numberOfUsers > 0 ? numberOfUsers : 0} user{numberOfUsers > 1 ? 's' : ''} x{' '}
                {selectedInterval === 'year' ? '12' : '1'} month{selectedInterval === 'year' ? 's' : ''}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={80} height={16} />
            ) : (
              <>
                <Typography color="grey.700" variant="body1" fontWeight={500} textAlign="right">
                  $
                  {!currentSubscriptionHasNonTrialFreePlan && (isTrial || numberOfUsers <= 0)
                    ? 0
                    : currentSubscriptionHasNonTrialFreePlan || !isSubscriptionUpgrading
                      ? totalForPlansWithSeats
                      : totals.totalAmountPerPlan}{' '}
                  USD&nbsp;
                  {!isSubscriptionUpgrading && `/ ${selectedInterval === 'year' ? 'year' : 'month'}`}
                </Typography>
                {!currentSubscriptionHasNonTrialFreePlan &&
                  isSubscriptionUpgrading &&
                  (isTrial || !isManageUsageScreen) && (
                    <Typography color="graygreen.700" variant="caption" fontWeight={500} textAlign="right">
                      ${numberOfUsers > 0 ? totalForPlansWithSeats : 0} USD /{' '}
                      {isTrial ? 'after trial ends' : selectedInterval === 'year' ? 'year after' : 'month after'}
                    </Typography>
                  )}
              </>
            )}
          </Box>
        </Box>
      )}

      {isManageUsageScreen && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingY: 2,
            borderBottom: '1px dotted',
            borderColor: 'grey.300',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography color="grey.700" variant="body1">
              Additional users
              {isTrial && (
                <Chip
                  label="Free trial"
                  sx={{
                    marginLeft: 1,
                    borderRadius: '16px',
                    backgroundColor: '#e5edf7',
                    color: '#31587c',
                    fontSize: '12px',
                    height: '24px',
                  }}
                />
              )}
            </Typography>

            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={240} height={16} sx={{ marginTop: 1 }} />
            ) : (
              <Typography color="graygreen.700" variant="caption">
                ${pricePerMonthForSeat} USD x {amountOfAdditionalSeats > 0 ? amountOfAdditionalSeats : 0} user
                {amountOfAdditionalSeats > 1 ? 's' : ''} x {selectedInterval === 'year' ? '12' : '1'} month
                {selectedInterval === 'year' ? 's' : ''}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={80} height={16} />
            ) : (
              <>
                <Typography color="grey.700" variant="body1" fontWeight={500} textAlign="right">
                  $
                  {isTrial || totalForSeats <= 0
                    ? 0
                    : !isSubscriptionUpgrading
                      ? totalForSeats
                      : totals.totalAmountForSeats}{' '}
                  USD&nbsp;
                  {!isSubscriptionUpgrading && `/ ${selectedInterval === 'year' ? 'year' : 'month'}`}
                </Typography>
                {(isSubscriptionUpgrading || isTrial) && (
                  <Typography color="graygreen.700" variant="caption" fontWeight={500} textAlign="right">
                    ${totalForSeats > 0 ? totalForSeats : 0} USD /{' '}
                    {isTrial ? 'after trial ends' : selectedInterval === 'year' ? 'year after' : 'month after'}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {isPremium && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingY: 2,
            borderBottom: '1px dotted',
            borderColor: 'grey.300',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography color="grey.700" variant="body1">
              Additional projects
              {isTrial && (
                <Chip
                  label="Free trial"
                  sx={{
                    marginLeft: 1,
                    borderRadius: '16px',
                    backgroundColor: '#e5edf7',
                    color: '#31587c',
                    fontSize: '12px',
                    height: '24px',
                  }}
                />
              )}
            </Typography>

            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={240} height={16} sx={{ marginTop: 1 }} />
            ) : (
              <Typography color="graygreen.700" variant="caption">
                ${pricePerMonthForProject} USD x {amountOfAdditionalProjects > 0 ? amountOfAdditionalProjects : 0}{' '}
                project
                {amountOfAdditionalProjects > 1 ? 's' : ''} x {selectedInterval === 'year' ? '12' : '1'} month
                {selectedInterval === 'year' ? 's' : ''}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={80} height={16} />
            ) : (
              <>
                <Typography color="grey.700" variant="body1" fontWeight={500} textAlign="right">
                  $
                  {!currentSubscriptionHasNonTrialFreePlan && (isTrial || totalForProjects <= 0)
                    ? 0
                    : currentSubscriptionHasNonTrialFreePlan || !isSubscriptionUpgrading
                      ? totalForProjects
                      : totals.totalAmountForProjects}{' '}
                  USD&nbsp;
                  {!isSubscriptionUpgrading && `/ ${selectedInterval === 'year' ? 'year' : 'month'}`}
                </Typography>
                {!currentSubscriptionHasNonTrialFreePlan && (isSubscriptionUpgrading || isTrial) && (
                  <Typography color="graygreen.700" variant="caption" fontWeight={500} textAlign="right">
                    ${totalForProjects > 0 ? totalForProjects : 0} USD /{' '}
                    {isTrial ? 'after trial ends' : selectedInterval === 'year' ? 'year after' : 'month after'}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {isSubscriptionUpgrading && Number(credits) < 0 && (
        <Box sx={{ paddingY: 2, borderBottom: '1px dotted', borderColor: 'grey.300' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography color="grey.700" variant="body1">
              Credits
            </Typography>

            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={100} height={26} />
            ) : (
              <Typography color="grey.700" variant="body1" fontWeight={500} textAlign="right">
                {Number(credits).toFixed(2).replace(/-/g, '-$')} USD
              </Typography>
            )}
          </Box>

          <Typography color="graygreen.700" variant="caption">
            Unused amount from current plan
          </Typography>
        </Box>
      )}

      <Box sx={{ paddingY: 2, borderBottom: '1px dotted', borderColor: 'grey.300' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography color="grey.700" variant="body1" fontWeight={600}>
            Total due today
          </Typography>
          {isLoading ? (
            <Skeleton animation="wave" variant="rounded" width={100} height={26} />
          ) : (
            <Typography color="primary.800" variant="h3" fontWeight={600} textAlign="right">
              ${isTrial ? 0 : totalAmount} USD
            </Typography>
          )}
        </Box>

        {!isLoading && !isCanceledSubscription && (
          <Typography
            color="graygreen.700"
            variant="caption"
            fontWeight={500}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            ${Number(totalAmountAfterRenewal) > 0 ? totalAmountAfterRenewal : 0} USD /{' '}
            {isTrial ? 'after trial ends' : selectedInterval === 'year' ? 'year after' : 'month after'}
          </Typography>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ marginTop: 2 }}>
          <Skeleton animation="wave" variant="rounded" width={343} height={12} sx={{ marginBottom: 1.25 }} />
          <Skeleton animation="wave" variant="rounded" width={220} height={12} sx={{ marginBottom: 2 }} />

          <Skeleton animation="wave" variant="rounded" width={343} height={12} sx={{ marginBottom: 1.25 }} />
          <Skeleton animation="wave" variant="rounded" width={220} height={12} sx={{ marginBottom: 2 }} />

          <Skeleton animation="wave" variant="rounded" width={343} height={12} sx={{ marginBottom: 1.25 }} />
          <Skeleton animation="wave" variant="rounded" width={220} height={12} />
        </Box>
      ) : (
        <>
          {!isAmountExceeded && (
            <Box sx={{ marginTop: 2 }}>
              {!isCanceledSubscription &&
              ((!isTrial && selectedInterval !== currentPlanProvider?.plan.interval) ||
                (isSubscriptionUpgrading !== undefined &&
                  (planDetails.plan !== currentPlanName ||
                    maximumNumberOfUsers !== numberOfUsers ||
                    maximumNumberOfProjects !== numberOfProjects))) ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'grey.500',
                    '& > span': { color: 'grey.800' },
                    '& > span:last-of-type': { color: 'grey.700' },
                  }}
                >
                  {!isSubscriptionUpgrading ? (
                    <>
                      Please note that the{' '}
                      <span>
                        {selectedInterval !== currentPlanProvider?.plan.interval ? 'change' : 'downgrade'} will take
                        effect
                      </span>{' '}
                      at the end of your current billing cycle on&nbsp;
                      <span>{endDate ? moment.unix(endDate).utc().format('MMMM DD, YYYY') : formattedRenewDate}</span>,
                      and <span>no refunds</span> will be issued. You will retain access to all features of your current
                      paid plan until the billing cycle ends.
                      <br />
                      <br />
                      The payment for the updated number of seats and usage will be reflected in your next&nbsp;
                      {selectedInterval === 'year' ? 'annual' : 'monthly'} renewal invoice, with the plan renewing
                      at&nbsp;
                      <span>${Number(totalAmountAfterRenewal) > 0 ? totalAmountAfterRenewal : 0}</span> USD on&nbsp;
                      <span>{endDate ? moment.unix(endDate).utc().format('MMMM DD, YYYY') : formattedRenewDate}</span>.
                      You may cancel your subscription at any time. The total includes any applicable discounts or
                      prorated adjustments. By proceeding, you agree to our <TermOfUseButton />
                    </>
                  ) : (
                    <>
                      Please note that the <span>upgrade will take effect immediately</span>. Payment for additional
                      seats{currentPlanName === WorkspaceSubscriptionPlan.Premium && ', projects,'} and usage,&nbsp;
                      calculated proportionally to the remaining days of your current billing cycle, will be charged
                      along with the cost of your new plan when you start your new subscription. Any <span>unused</span>{' '}
                      amount from your current plan, also proportional to the remaining days of your billing cycle, will{' '}
                      <span>automatically be credited</span> towards the total cost. Your updated&nbsp;
                      <span>
                        plan will renew on{' '}
                        {!currentSubscriptionHasNonTrialFreePlan && endDate
                          ? moment.unix(endDate).utc().format('MMMM DD, YYYY')
                          : formattedRenewDate}
                      </span>
                      &nbsp; at your standard <span>{selectedInterval === 'year' ? 'annual' : 'monthly'}</span> billing
                      rate of <span>${Number(totalAmountAfterRenewal) > 0 ? totalAmountAfterRenewal : 0} USD</span>. You
                      may cancel your subscription at any time. The total includes any applicable discounts or prorated
                      adjustments. By proceeding, you agree to our <TermOfUseButton />
                    </>
                  )}
                </Typography>
              ) : !isTrial || isCanceledSubscription ? (
                <Typography color="grey.500" variant="body2">
                  Your plan will renew at{' '}
                  <b>${Number(totalAmountAfterRenewal) > 0 ? totalAmountAfterRenewal : 0} USD</b> on&nbsp;
                  <b>
                    {isCanceledSubscription
                      ? formattedRenewDate
                      : endDate
                        ? moment.unix(endDate).utc().format('MMMM DD, YYYY')
                        : formattedRenewDate}
                  </b>
                  . Cancel your subscription anytime. Your total includes any discounts or prorated prices. By
                  proceeding you agree to our <TermOfUseButton />
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'grey.500',
                    '& > span:first-of-type': { color: 'grey.800' },
                    '& > span:last-of-type': { color: 'grey.700' },
                  }}
                >
                  After your trial ends, you will be charged{' '}
                  <span>${Number(totalAmountAfterRenewal) > 0 ? totalAmountAfterRenewal : 0} USD</span>&nbsp;
                  <span>
                    per {selectedInterval === 'year' ? 'year' : 'month'} starting{' '}
                    {endDate ? moment.unix(endDate).utc().format('MMMM DD, YYYY') : formattedRenewDate}
                  </span>
                  .&nbsp; You can always cancel before then. By proceeding you agree to our <TermOfUseButton />
                </Typography>
              )}
            </Box>
          )}

          {isAmountExceeded && (
            <Alert
              sx={{ mt: 1.5 }}
              severity="error"
              className="error"
              description={
                <Typography>
                  The total amount must not exceed $999,999.99 USD.&nbsp; Please reduce the number of users or choose
                  another plan.
                </Typography>
              }
              variant="outlined"
            />
          )}
        </>
      )}
    </Box>
  )
}
